import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, type Ingest, type StreamKey, type StreamKeyWithSecret } from '../api';
import { DangerLink, PrimaryButton, SecondaryButton } from './ui';
import { ObsAppsHints } from './AppsHints';
import { IngestRegionPicker } from './IngestRegion';
import { ObsConnection } from './ObsConnection';
import { useObsProtocol } from './srt';
import { KeyGrace } from './KeyGrace';
import { KeySlate } from './KeySlate';
import { VerticalInput } from './VerticalInput';

/**
 * OBS connection settings: protocol (RTMPS / SRT), server and stream key or the
 * SRT address (secrets fetched only on Show / Copy, see ObsConnection), new key
 * with an inline confirmation, recommended settings.
 * Desktop: popover under the source node. Phone: inside the source widget.
 */
export function ObsSettings({ streamName, protocol, onDone, heading = true }: { streamName?: string; protocol?: string; onDone: () => void; heading?: boolean }) {
  const qc = useQueryClient();
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest') });
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') });
  const mains = keys.data?.filter((k) => k.role !== 'vertical');
  const key = mains?.find((k) => k.stream_name === streamName && !k.revoked_at) ?? mains?.find((k) => !k.revoked_at) ?? mains?.[0];
  const [proto, setProto] = useObsProtocol(protocol);
  const [epoch, setEpoch] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const rotate = async () => {
    if (!key) return;
    setBusy(true);
    setErr('');
    try {
      // The answer carries the new key (and SRT address): not kept, Show fetches it again.
      await post<StreamKeyWithSecret>(`/api/stream-keys/${key.id}/rotate`);
      setEpoch((e) => e + 1);
      setConfirm(false);
      await qc.invalidateQueries({ queryKey: ['stream-keys'] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {heading && <div className="font-mono text-[11px] font-medium tracking-[.12em] text-accent">OBS SETTINGS</div>}
      <IngestRegionPicker ingest={ingest.data} compact />
      <ObsConnection ingest={ingest.data} streamKey={key} proto={proto} onProto={setProto} epoch={epoch} />
      {/* VERT-1 (mockup B1 / B2, VT-15): the vertical input lives under the main key */}
      <VerticalInput streamName={key?.stream_name ?? streamName} ingest={ingest.data} />
      {key?.revoked_at && <div className="text-xs text-fail">This key is revoked. Issue a new key to stream again.</div>}
      {!!ingest.data?.recommended.length && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted">Recommended in OBS</span>
          <ul className="flex flex-wrap gap-1.5">
            {ingest.data.recommended.map((t) => (
              <li key={t} className="rounded-md border border-line px-2 py-[3px] font-mono text-xs text-text-soft">{t}</li>
            ))}
          </ul>
        </div>
      )}
      {key && <KeyGrace key={key.id} streamKey={key} ingest={ingest.data} />}
      {key && <KeySlate key={`slate-${key.id}`} streamKey={key} ingest={ingest.data} />}
      {err && <div role="alert" className="text-xs text-fail">{err}</div>}
      {confirm ? (
        <div role="alertdialog" aria-label="Issue a new stream key?" className="flex flex-col gap-2.5 rounded-md border border-fail/50 bg-fail/[.07] p-3 text-fail">
          <div className="font-medium">Issue a new stream key?</div>
          <div className="text-[13px] text-text-soft">The current key stops working for new connections. Your broadcast continues; paste the new key into OBS before you restart it.</div>
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton onClick={() => setConfirm(false)}>Cancel</SecondaryButton>
            <button type="button" disabled={busy} onClick={() => void rotate()} className="min-h-[38px] rounded-md border border-fail bg-fail/[.12] text-sm font-medium text-fail">
              Issue new key
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2.5">
          <DangerLink disabled={!key} onClick={() => setConfirm(true)}>{key?.revoked_at ? 'Issue a new key' : 'Issue a new stream key'}</DangerLink>
          <PrimaryButton onClick={onDone}>Done</PrimaryButton>
        </div>
      )}
      <ObsAppsHints />
    </div>
  );
}
